const CANONICAL_SECTION_ORDER = [
  'OVERVIEW',
  'RESPONSIBILITIES',
  'EXPERIENCE AND SPECIALIZATION',
  'ADDITIONAL INFORMATION',
  'BENEFITS',
  'CREDENTIALS'
];

function cleanString(value) {
  return String(value ?? '').trim();
}

function toArray(value) {
  if (Array.isArray(value)) {
    return value
      .map(item => cleanString(item))
      .filter(Boolean);
  }

  if (value === undefined || value === null) {
    return [];
  }

  const text = cleanString(value);

  if (!text) {
    return [];
  }

  return text
    .split('\n')
    .map(item =>
      item
        .replace(/^[-•]\s*/, '')
        .trim()
    )
    .filter(Boolean);
}

function normalizeSectionName(name) {
  return cleanString(name)
    .toUpperCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
}

function normalizeSubheadingName(name) {
  return cleanString(name)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ');
}

function normalizeSections(sections = {}) {
  const normalized = {};

  Object.entries(sections || {}).forEach(
    ([sectionName, sectionValue]) => {

      const cleanSectionName =
        normalizeSectionName(sectionName);

      if (!cleanSectionName || !sectionValue) {
        return;
      }

      const cleanSection = {};

      Object.entries(sectionValue).forEach(
        ([subheadingName, subheadingValue]) => {

          const cleanSubheadingName =
            normalizeSubheadingName(subheadingName);

          const values =
            toArray(subheadingValue);

          if (
            cleanSubheadingName &&
            values.length > 0
          ) {
            cleanSection[cleanSubheadingName] = values;
          }
        }
      );

      if (Object.keys(cleanSection).length > 0) {
        normalized[cleanSectionName] = cleanSection;
      }
    }
  );

  CANONICAL_SECTION_ORDER.forEach(sectionName => {
    if (!normalized[sectionName]) {
      normalized[sectionName] = {};
    }
  });

  return normalized;
}

function firstSectionValue(
  sections,
  sectionName,
  subheadingNames = []
) {

  const section =
    sections[sectionName];

  if (!section) {
    return '';
  }

  for (const subheadingName of subheadingNames) {

    const values =
      section[subheadingName];

    if (
      Array.isArray(values) &&
      values.length > 0
    ) {
      return values.join(', ');
    }
  }

  return '';
}

function buildLegacySections(job) {

  const sections = {
    OVERVIEW: {
      Languages: toArray(job.languages),
      Education: toArray(job.education),
      Experience: toArray(job.experience),
      'On site': toArray(
        job.workplaceType === 'On site'
          ? job.workplaceDescription
          : ''
      ),
      Hybrid: toArray(
        job.workplaceType === 'Hybrid'
          ? job.workplaceDescription
          : ''
      ),
      'On the road': toArray(
        job.workplaceType === 'On the road'
          ? job.workplaceDescription
          : ''
      ),
      'Work setting': toArray(job.workSetting),
      'Work site environment':
        toArray(job.workSiteEnvironment),
      'Budgetary responsibility':
        toArray(job.budgetaryResponsibility)
    },

    RESPONSIBILITIES: {
      Tasks: toArray(
        job.tasks || job.responsibilities
      ),
      Supervision: toArray(job.supervision)
    },

    'EXPERIENCE AND SPECIALIZATION': {
      'Computer and technology knowledge':
        toArray(
          job.computerAndTechnologyKnowledge ||
          job.technologySkills
        ),
      'Technical terminology':
        toArray(job.technicalTerminology),
      'Area of specialization':
        toArray(job.areaOfSpecialization)
    },

    'ADDITIONAL INFORMATION': {
      'Transportation/travel information':
        toArray(job.transportationTravelInformation),
      'Work conditions and physical capabilities':
        toArray(job.workConditionsAndPhysicalCapabilities),
      'Own tools/equipment':
        toArray(job.ownToolsEquipment),
      'Personal suitability':
        toArray(
          job.personalSuitability ||
          job.suitability
        ),
      'Security and safety':
        toArray(job.securityAndSafety),
      'Weight handling':
        toArray(job.weightHandling)
    },

    BENEFITS: {
      'Financial benefits':
        toArray(job.financialBenefits),
      'Health benefits':
        toArray(job.healthBenefits),
      'Long term benefits':
        toArray(job.longTermBenefits),
      'Other benefits':
        toArray(job.otherBenefits)
    },

    CREDENTIALS: {
      'Certificates, licences, memberships, and courses':
        toArray(
          job.certificatesLicencesMembershipsCourses ||
          job.credentials
        )
    }
  };

  return normalizeSections(sections);
}

function buildLocation(job) {

  const existingLocation =
    cleanString(job.location);

  if (existingLocation) {
    return existingLocation;
  }

  const city =
    cleanString(job.city) || 'Spruce Grove';

  const province =
    cleanString(job.province) || 'AB';

  return `${city}, ${province}`;
}

function getWorkplaceType(job, sections) {

  const direct =
    cleanString(job.workplaceType || job.type);

  if (direct) {
    return direct;
  }

  const overview =
    sections.OVERVIEW || {};

  if (overview['On site']?.length) {
    return 'On site';
  }

  if (overview.Hybrid?.length) {
    return 'Hybrid';
  }

  if (overview['On the road']?.length) {
    return 'On the road';
  }

  return '';
}

export function normalizeJob(job = {}) {

  const sections =
    normalizeSections(
      Object.keys(job.sections || {}).length > 0
        ? job.sections
        : buildLegacySections(job)
    );

  const title =
    cleanString(job.title);

  const company =
    cleanString(job.company);

  const location =
    buildLocation(job);

  const city =
    cleanString(job.city) || '';

  const province =
    cleanString(job.province) || '';

  const workplaceType =
    getWorkplaceType(job, sections);

  const education =
    firstSectionValue(
      sections,
      'OVERVIEW',
      ['Education']
    );

  const experience =
    firstSectionValue(
      sections,
      'OVERVIEW',
      ['Experience']
    );

  const workSetting =
    firstSectionValue(
      sections,
      'OVERVIEW',
      ['Work setting']
    );

  return {
    title,

    normalizedTitle:
      title.toLowerCase(),

    company,

    normalizedCompany:
      company.toLowerCase(),

    location,

    city,

    province,

    postalCode:
      cleanString(job.postalCode),

    salary:
      cleanString(job.salary),

    hoursPerWeek:
      cleanString(job.hoursPerWeek || job.hours),

    employmentType:
      cleanString(job.employmentType),

    workplaceType,

    type:
      cleanString(job.type || workplaceType),

    postingDate:
      cleanString(job.postingDate),

    jobId:
      cleanString(job.jobId),

    postingNumericId:
      cleanString(job.postingNumericId),

    distance:
      cleanString(job.distance),

    isNew:
      Boolean(job.isNew),

    description:
      cleanString(job.description),

    sections,

    source:
      cleanString(job.source) || 'local',

    sourceUrl:
      cleanString(
        job.sourceUrl ||
        job.url
      ),

    url:
      cleanString(
        job.url ||
        job.sourceUrl
      ),

    // Backward-compatible read fields.
    education,
    experience,
    workSetting,

    responsibilities:
      sections.RESPONSIBILITIES?.Tasks || [],

    technologySkills:
      sections['EXPERIENCE AND SPECIALIZATION']
        ?.['Computer and technology knowledge'] || [],

    suitability:
      sections['ADDITIONAL INFORMATION']
        ?.['Personal suitability'] || []
  };
}
